import { Container, Graphics } from 'pixi.js';
import { place, type RoomContext, type RoomDecorator } from '../index';
import * as kit from '../../castle-kit';

/**
 * THRONE HALL (Тронный зал) — THE HERO HALL of Castle Hearthhold, composed for ceremonial CALM, not
 * clutter. One unmistakable focal climax: the communal THRONE on its stepped DAIS, the heraldic ROSE
 * WINDOW glowing behind it and the two LARGEST banners in the world flanking it — the brightest light
 * pool in the whole keep. A royal blue-and-gold RUNNER draws an UNOBSTRUCTED ceremonial spine straight
 * from the south door up to the dais (the eye sweeps it clean — nothing planted on the centre axis).
 *
 * Supporting beats, all pushed to the EDGES so the centre breathes: the GREAT HEARTH pools warm light
 * off-axis in the SW, two trestle FEAST tables (each ringed by four bench seats) sit symmetric to W/E,
 * a tidy pair of release TROPHY shields crowns the north wall, and a restrained colonnade of corner
 * pillars + two chandeliers + flanking banners + a few wall torches frame the chamber. Light is
 * deliberate — the dais owns the brightest pool, the hearth the second; everything else is edge warmth.
 *
 * Geometry: zone (1180,760) 1240×560 → centre (1800,1040). Doors N@x1800, S@x1800, W@y1040, E@y1040
 * kept clear (central cross + each door mouth walkable).
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { art, mix, lighten, darken } = ctx;

  // ── Floor: the royal ceremonial runner down the N–S spine (laid FIRST, under everything). ──
  place(ctx, kit.runnerRug(art, 280, 520), 1800, 1040);

  // ── HERO: the raised stepped DAIS carrying the communal THRONE — its glow pool is the BRIGHTEST beat
  //    in the whole keep, the rose window crowning it and the two largest banners flanking it. ──
  place(ctx, kit.roseWindow(art, 98), 1800, 806);
  const dais = new Container();
  kit.glowPool(dais, 0, -10, 215, art, 1.9); // single brightest, widest warm pool — outshines the hearth
  const stone = mix(0x6f6554, art.wall, 0.5);
  dais.addChild(
    new Graphics()
      .ellipse(0, 40, 168, 60).fill({ color: 0x000000, alpha: 0.16 }) // dais cast shadow
      .ellipse(0, 30, 156, 56).fill({ color: darken(stone, 0.22) }) // lower step
      .ellipse(0, 16, 124, 46).fill({ color: stone }) // mid step
      .ellipse(0, 16, 124, 46).stroke({ color: mix(0xd9a63a, art.accent, 0.5), width: 2, alpha: 0.5 }) // gold rim
      .ellipse(0, 4, 96, 36).fill({ color: lighten(stone, 0.08) }), // top platform
  );
  place(ctx, dais, 1800, 985);
  const throne = kit.throne(art);
  throne.scale.set(1.7); // scaled up so it unmistakably dominates the hall (the climax of the castle)
  place(ctx, throne, 1800, 950); // the 'guild-throne' sit anchor
  place(ctx, kit.banner(art, 0x8a2f2f, 156), 1648, 792); // the two LARGEST banners in the keep
  place(ctx, kit.banner(art, 0x2f4a8a, 156), 1952, 792);
  // A symmetric PAIR of standing braziers framing the dais (never on the centre axis / runner spine).
  place(ctx, kit.brazier(art), 1672, 1016);
  place(ctx, kit.brazier(art), 1928, 1016);

  // ── FIRE: the GREAT HEARTH off-axis in the SW — the secondary warm gather-pool + walk-into hazard.
  //    A standing CANDELABRA at the mirror point (SE) balances its visual weight so the lower hall reads
  //    symmetric about the throne axis (the hearth stays the namesake feature; the candelabra answers it). ──
  place(ctx, kit.greatHearth(art), 1500, 1150);
  place(ctx, kit.candelabra(art), 2100, 1150);

  // ── Two trestle FEAST tables, each ringed by four bench seats (sit anchors), symmetric W/E. ──
  place(ctx, kit.feastTable(art, 180), 1430, 905);
  for (const [sx, sy] of [
    [1380, 880],
    [1480, 880],
    [1380, 930],
    [1480, 930],
  ] as const) {
    place(ctx, kit.pew(art, 70), sx, sy);
  }
  place(ctx, kit.feastTable(art, 180), 2170, 905);
  for (const [sx, sy] of [
    [2120, 880],
    [2220, 880],
    [2120, 930],
    [2220, 930],
  ] as const) {
    place(ctx, kit.pew(art, 70), sx, sy);
  }

  // ── A tidy, MATCHED pair of release TROPHY shields crowning the north wall (axial heraldic symmetry). ──
  place(ctx, kit.trophyShield(art, 0x8a2f2f), 1340, 800);
  place(ctx, kit.trophyShield(art, 0x8a2f2f), 2260, 800);

  // ── Restrained colonnade: pillars at the four interior corners frame the spine + dais. ──
  for (const [px, py] of [
    [1300, 820],
    [1300, 1260],
    [2300, 820],
    [2300, 1260],
  ] as const) {
    place(ctx, kit.pillar(art), px, py);
  }

  // ── Two chandeliers, one over each feast cluster (warm overhead onto the gather spots). ──
  place(ctx, kit.chandelier(art), 1600, 920);
  place(ctx, kit.chandelier(art), 2000, 1160);

  // ── A flanking banner pair on the long walls (one warm, one cool — heraldic symmetry). ──
  place(ctx, kit.banner(art, 0x8a2f2f, 100), 1300, 1100);
  place(ctx, kit.banner(art, 0x2f4a8a, 100), 2300, 1100);

  // ── A restrained pair of wall torches for edge warmth, kept OFF the door mouths. ──
  place(ctx, kit.wallTorch(art), 1400, 772);
  place(ctx, kit.wallTorch(art), 2200, 772);
};

export default decorate;
